import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button, Dropdown, Input, Modal,
} from "antd";
import debounce from "lodash/debounce";
import Highlighter from "react-highlight-words";
import { toast } from "react-toastify";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import SampleSubmissionConfirmModal from "../SampleSubmissionModal/SampleSubmissionConfirmModal";

import getTestName, { getGroupName, getGroupTests } from "./helpers";

import { getTestDirectory, TEST_SUGGESTION_CATEGORY } from "../../../actions/testDirectory";

import { ReactComponent as MoreIcon } from "../../../assets/images/sample-submission/moreIcon.svg";
import searchIcon from "../../../assets/images/sample-submission/searchIcon.svg";

import { designTokens } from "../../../styles/tokens";

import "../MainPage/Submissions/ActionsMenu.css";
import "../SampleSubmissionModal/AddTestModal.css";
import "../SubmissionForm/SampleDetailsPage/AnalysisRequest/AnalysisRequest.css";
import "./TestDirectory.css";
import "./TestDirectoryModals.css";

/**
 * Groups tab panel — Available Tests sidebar + group cards board.
 * Inline create uses createDraft; CreateGroupModal (exported below) is used elsewhere.
 */
const DRAG_TESTS_MIME = "application/x-test-directory-tests";

function parseDraggedTests(event) {
  const raw = event.dataTransfer.getData(DRAG_TESTS_MIME)
    || event.dataTransfer.getData("text/plain");
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [String(parsed)];
  } catch (e) {
    return raw ? [raw] : [];
  }
}

export default function GroupsPanel({
  className = "",
  sampleCategory = "product",
  groups = [],
  canModifyGroups = true,
  createDraft = null,
  activeKey = null,
  onActiveKeyChange,
  onNewGroupClick,
  onAddTestsToGroup,
  onRemoveTestFromGroup,
  onRenameRequest,
  onDeleteRequest,
  existingGroupsReadonly = false,
}) {
  const [testSearch, setTestSearch] = useState("");
  const [availableTestNames, setAvailableTestNames] = useState([]);
  const [loadingAvailableTests, setLoadingAvailableTests] = useState(true);
  const [selectedTests, setSelectedTests] = useState({});
  const isMountedRef = useRef(true);
  const searchRequestIdRef = useRef(0);
  const isCreateMode = Boolean(createDraft);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAvailableTests = useCallback(async (searchInput = "") => {
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setLoadingAvailableTests(true);

    const result = await getTestDirectory(
      searchInput,
      sampleCategory,
      undefined,
      undefined,
      TEST_SUGGESTION_CATEGORY.CUSTOMER_MAPPING,
    );
    if (!isMountedRef.current || requestId !== searchRequestIdRef.current) return;

    if (!result.success) {
      toast.error(result.message || "Unable to load available tests.");
      setAvailableTestNames([]);
    } else {
      const nextList = Array.isArray(result.customerTests) && result.customerTests.length
        ? result.customerTests
        : (result.tests || []);
      setAvailableTestNames(nextList.map(getTestName).filter(Boolean));
    }
    setLoadingAvailableTests(false);
  }, [sampleCategory]);

  const debouncedSearch = useMemo(
    () => debounce((searchInput) => {
      fetchAvailableTests(searchInput);
    }, 500),
    [fetchAvailableTests],
  );

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAvailableTests("");
  }, [fetchAvailableTests]);

  const selectedNames = Object.keys(selectedTests);
  const selectedCount = selectedNames.length;

  const handleChangeTestSearch = (e) => {
    const value = e.target.value;
    setTestSearch(value);
    debouncedSearch(value);
  };

  const clearTestSearch = () => {
    setTestSearch("");
    debouncedSearch.cancel();
    fetchAvailableTests("");
  };

  const toggleSelected = (name) => {
    setSelectedTests((prev) => {
      const next = { ...prev };
      if (next[name]) delete next[name];
      else next[name] = true;
      return next;
    });
  };

  const clearSelection = () => setSelectedTests({});

  const onDragStart = (event, testName) => {
    const names = selectedTests[testName] && selectedCount > 0
      ? selectedNames
      : [testName];
    const payload = JSON.stringify(names);
    event.dataTransfer.setData(DRAG_TESTS_MIME, payload);
    event.dataTransfer.setData("text/plain", payload);
    event.dataTransfer.effectAllowed = "copy";
  };

  const handleDropOnGroup = (event, targetKey) => {
    event.preventDefault();
    if (!canModifyGroups) return;
    const names = parseDraggedTests(event);
    if (!names.length) return;
    if (typeof onActiveKeyChange === "function") onActiveKeyChange(targetKey);
    if (typeof onAddTestsToGroup === "function") onAddTestsToGroup(targetKey, names);
    clearSelection();
  };

  const handleDropOnDraft = (event) => {
    event.preventDefault();
    if (!createDraft || typeof createDraft.onAddTests !== "function") return;
    if (typeof onActiveKeyChange === "function") onActiveKeyChange("draft");
    const names = parseDraggedTests(event);
    if (!names.length) return;
    createDraft.onAddTests(names);
    clearSelection();
  };

  const menuItems = (group, name, key) => ([
    {
      key: "rename",
      label: (
        <div
          className="SampleSubmission__ActionsMenuItem"
          onClick={() => {
            if (typeof onActiveKeyChange === "function") onActiveKeyChange(key);
            if (typeof onRenameRequest === "function") onRenameRequest({ group, name });
          }}
        >
          <span>Rename</span>
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          className="SampleSubmission__ActionsMenuItem"
          onClick={() => {
            if (typeof onActiveKeyChange === "function") onActiveKeyChange(key);
            if (typeof onDeleteRequest === "function") onDeleteRequest({ group, name });
          }}
        >
          <span>Delete</span>
        </div>
      ),
    },
  ]);

  const renderSelectedTests = (tests, onRemove, { showRemoveOnSingle = false } = {}) => {
    const names = tests.map((test) => getTestName(test)).filter(Boolean);
    if (!names.length) {
      return <div className="TestDirectory__GroupEmptyDrop">Drag and drop tests</div>;
    }
    return (
      <div className="analysis-request-tests-selected-container TestDirectory__GroupSelectedContainer">
        <StyledScrollbar noScrollX className="TestDirectory__GroupSelectedScrollbar">
          <ul className="analysis-request-tests-selected-list">
            {names.map((testName, index) => (
              <li key={`${testName}-${index}`} className="analysis-request-tests-selected-list-item">
                <span className="TestDirectory__GroupChipLabel">{testName}</span>
                {typeof onRemove === "function" && (showRemoveOnSingle || names.length > 1) && (
                  <div
                    className="AnalysisRequest__CloseIcon"
                    role="button"
                    tabIndex={0}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(testName);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove(testName);
                      }
                    }}
                  >
                    <CloseOutlined className="TestDirectoryModal__InputClearIcon" aria-hidden />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </StyledScrollbar>
      </div>
    );
  };

  const renderDraftCard = () => {
    if (!createDraft) return null;
    const {
      name = "",
      tests = [],
      onNameChange,
      onRemoveTest,
      saving = false,
      onSave,
      onCancel,
      error = "",
    } = createDraft;

    return (
      <div
        className={`TestDirectory__GroupCard TestDirectory__GroupCard--draft${activeKey === "draft" || activeKey == null ? " TestDirectory__GroupCard--active" : ""}`}
        onClick={() => {
          if (typeof onActiveKeyChange === "function") onActiveKeyChange("draft");
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={handleDropOnDraft}
      >
        <div className="TestDirectory__GroupCardInner">
          <div className="TestDirectory__GroupCardNameRow">
            <Input
              value={name}
              onChange={(e) => {
                if (typeof onNameChange === "function") onNameChange(e.target.value);
              }}
              placeholder="Enter Name*"
              className="TestDirectory__GroupNameInput"
              onPressEnter={onSave}
              disabled={saving}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className={`TestDirectory__GroupCardBody TestDirectory__GroupCardBody--draft${tests.length ? " TestDirectory__GroupCardBody--filled" : ""}`}>
            {renderSelectedTests(tests, onRemoveTest, { showRemoveOnSingle: true })}
          </div>
          <footer
            className="TestDirectory__GroupCardFooter"
            onClick={(e) => e.stopPropagation()}
          >
            {error ? (
              <small className="AddTestInput__Error TestDirectoryModal__CreateGroupError">{error}</small>
            ) : null}
            <div className="AddTestBtn TestDirectory__GroupCardFooterActions">
              {typeof onCancel === "function" && (
                <Button
                  type="default"
                  htmlType="button"
                  onClick={onCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="primary"
                htmlType="button"
                onClick={onSave}
                loading={saving}
                disabled={saving || (!name.trim() && !tests.length)}
              >
                Save
              </Button>
            </div>
          </footer>
        </div>
      </div>
    );
  };

  const panelClassName = ["TestDirectory__GroupsWorkspace", className].filter(Boolean).join(" ");

  return (
    <div className={panelClassName}>
      <aside className="TestDirectory__AvailableTests">
        <div className="TestDirectory__AvailableTestsHeader">
          <div className="TestDirectory__AvailableTestsTitleRow">
            <h3 className="TestDirectory__AvailableTestsTitle">Available Tests</h3>
          </div>
          <div className="TestDirectory__AvailableTestsSearchRow">
            <div className="analysis-request-search-box TestDirectory__AvailableTestsSearchBox">
              <Input
                allowClear
                value={testSearch}
                onChange={handleChangeTestSearch}
                onClear={clearTestSearch}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  debouncedSearch.cancel();
                  fetchAvailableTests(e.target.value);
                }}
                placeholder="Search anything..."
                className="TestDirectory__AvailableTestsSearchInput"
                prefix={<img alt="" src={searchIcon} aria-hidden />}
              />
            </div>
            <p className="TestDirectory__AvailableTestsHint">
              {selectedCount > 0
                ? "Drag and drop selected tests onto a group."
                : "Click to select tests, then drag and drop to add to a group."}
            </p>
            {selectedCount > 0 && (
              <Button
                type="link"
                className="TestDirectory__ClearSelection"
                onClick={clearSelection}
              >
                ✕ Clear Selection
              </Button>
            )}
          </div>
        </div>
        {loadingAvailableTests ? (
          <div className="TestDirectory__AvailableTestsBody">
            <div className="TestDirectory__Loading" aria-busy="true">
              <div className="TestDirectory__Skeleton ActiveloadingAnimation" />
            </div>
          </div>
        ) : (
          <div className="TestDirectory__AvailableTestsBody">
            <StyledScrollbar
              noScrollX
              className="TestDirectory__AvailableTestsScrollbar"
            >
              <ul className="analysis-request-smart-suggestions-list TestDirectory__AvailableTestsList">
                {availableTestNames.map((name) => {
                  const isSelected = Boolean(selectedTests[name]);
                  return (
                    <li
                      key={name}
                      className={isSelected ? "selected-test-suggestion" : "unselected-test-suggestion"}
                      draggable
                      onDragStart={(event) => onDragStart(event, name)}
                      onClick={() => toggleSelected(name)}
                    >
                      <Highlighter
                        highlightClassName="searchHighlight"
                        searchWords={[testSearch.trim()]}
                        autoEscape
                        textToHighlight={name}
                      />
                    </li>
                  );
                })}
                {!availableTestNames.length && (
                  <li className="TestDirectory__EmptyInline">No tests found</li>
                )}
              </ul>
            </StyledScrollbar>
          </div>
        )}
      </aside>

      <div className="TestDirectory__GroupsDivider" aria-hidden />

      <StyledScrollbar noScrollX className="TestDirectory__GroupsBoardScrollbar">
        <div className="TestDirectory__GroupsBoard">
          {isCreateMode ? renderDraftCard() : (
            canModifyGroups && typeof onNewGroupClick === "function" && (
              <button type="button" className="TestDirectory__NewGroupCard" onClick={onNewGroupClick}>
                <span className="TestDirectory__NewGroupCardContent">
                  <span className="TestDirectory__NewGroupIconWrap" aria-hidden>
                    <PlusOutlined className="TestDirectory__NewGroupIcon" />
                  </span>
                  <span className="TestDirectory__NewGroupLabel">New Group</span>
                </span>
              </button>
            )
          )}

          {groups.map((group, index) => {
            const name = getGroupName(group, index);
            const tests = getGroupTests(group);
            const key = String(index);
            const isReadonly = existingGroupsReadonly || !canModifyGroups;

            if (isReadonly) {
              return (
                <div
                  key={`${name}-${index}`}
                  className="TestDirectory__GroupCard TestDirectory__GroupCard--readonly"
                >
                  <div className="TestDirectory__GroupCardHeader">
                    <div className="TestDirectory__GroupNameWrap">
                      <h3 className="TestDirectory__GroupName">{name}</h3>
                    </div>
                    <span className="TestDirectory__GroupMoreBtn TestDirectory__GroupMoreBtn--readonly" aria-hidden>
                      <MoreIcon
                        width={20}
                        height={20}
                        fill={designTokens.colorContentBase}
                      />
                    </span>
                  </div>
                  <div className="TestDirectory__GroupCardBody">
                    {renderSelectedTests(tests)}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={`${name}-${index}`}
                className={`TestDirectory__GroupCard ${activeKey === key ? "TestDirectory__GroupCard--active" : ""}`}
                onClick={() => {
                  if (typeof onActiveKeyChange === "function") onActiveKeyChange(key);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => handleDropOnGroup(e, key)}
              >
                <div className="TestDirectory__GroupCardHeader">
                  <div className="TestDirectory__GroupNameWrap">
                    <h3 className="TestDirectory__GroupName">{name}</h3>
                  </div>
                  <Dropdown
                    menu={{ items: menuItems(group, name, key) }}
                    trigger={["click"]}
                    placement="bottomRight"
                    overlayClassName="SampleSubmission__ActionsMenu"
                  >
                    <button
                      type="button"
                      className="TestDirectory__GroupMoreBtn"
                      aria-label={`Actions for ${name}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreIcon
                        width={20}
                        height={20}
                        fill={designTokens.colorContentBase}
                        aria-hidden
                      />
                    </button>
                  </Dropdown>
                </div>
                <div className="TestDirectory__GroupCardBody">
                  {renderSelectedTests(
                    tests,
                    typeof onRemoveTestFromGroup === "function"
                      ? (testName) => onRemoveTestFromGroup({
                        group, testName, groupName: name, key,
                      })
                      : undefined,
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </StyledScrollbar>
    </div>
  );
}

export function CreateGroupModal({
  open,
  saving = false,
  sampleCategory = "product",
  groups = [],
  onClose,
  onCreate,
  onRename,
  onDelete,
  onUpdateTests,
}) {
  const [groupName, setGroupName] = useState("");
  const [groupTests, setGroupTests] = useState([]);
  const [error, setError] = useState("");
  const [activeKey, setActiveKey] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [removeTestTarget, setRemoveTestTarget] = useState(null);

  const canEditExistingGroups = typeof onUpdateTests === "function"
    || typeof onRename === "function"
    || typeof onDelete === "function";

  const resetForm = useCallback(() => {
    setGroupName("");
    setGroupTests([]);
    setError("");
    setActiveKey(null);
    setRenameTarget(null);
    setRenameValue("");
    setDeleteTarget(null);
    setRemoveTestTarget(null);
  }, []);

  useEffect(() => {
    if (open) {
      setActiveKey("draft");
    } else {
      resetForm();
    }
  }, [open, resetForm]);

  const addTests = (testNames) => {
    const names = (Array.isArray(testNames) ? testNames : [testNames])
      .map(getTestName)
      .filter(Boolean);
    if (!names.length) return;
    setGroupTests((current) => {
      const next = [...current];
      names.forEach((testName) => {
        if (!next.includes(testName)) next.push(testName);
      });
      return next;
    });
    setError("");
  };

  const removeGroupTest = (testName) => {
    setGroupTests((current) => current.filter((t) => t !== testName));
    setError("");
  };

  const handleSave = async () => {
    const name = groupName.trim();
    if (!name) {
      setError("Group name is required");
      return;
    }
    if (!groupTests.length) {
      setError("Add at least one test to the group");
      return;
    }
    if (typeof onCreate !== "function") return;
    const success = await onCreate({ group_name: name, test_list: groupTests });
    if (success !== false && typeof onClose === "function") {
      onClose();
    }
  };

  const addTestsToGroup = (targetKey, testNames) => {
    const names = (Array.isArray(testNames) ? testNames : [testNames])
      .map(getTestName)
      .filter(Boolean);
    if (!names.length || targetKey == null || typeof onUpdateTests !== "function") return;

    const group = groups[Number(targetKey)];
    if (!group) return;
    const current = getGroupTests(group).map(getTestName).filter(Boolean);
    const merged = [...current];
    names.forEach((testName) => {
      if (!merged.includes(testName)) merged.push(testName);
    });
    if (merged.length !== current.length) onUpdateTests(group, merged);
  };

  const removeTestFromGroup = (group, testName) => {
    if (typeof onUpdateTests !== "function") return;
    onUpdateTests(
      group,
      getGroupTests(group)
        .map(getTestName)
        .filter((name) => name && name !== testName),
    );
  };

  const submitRename = async () => {
    const nextName = renameValue.trim();
    if (!nextName || typeof onRename !== "function") return;
    const success = await onRename(renameTarget.group, nextName);
    if (success !== false) {
      setRenameTarget(null);
      setRenameValue("");
    }
  };

  return (
    <>
      <Modal
        open={open}
        closable={!saving}
        centered
        footer={null}
        width={1144}
        destroyOnHidden
        maskClosable={!saving}
        closeIcon={<CloseOutlined className="SampleSubmissionModal__closeIcon" />}
        className="TestDirectoryModal TestDirectoryModal--createGroup"
        onCancel={saving ? undefined : onClose}
      >
        <div className="TestDirectoryModal__CreateGroupShell">
          <header className="TestDirectoryModal__CreateGroupHeader">
            <h2 className="TestDirectoryModal__CreateGroupTitle">Create New Group</h2>
          </header>

          <div className="TestDirectoryModal__CreateGroupBody">
            {open ? (
              <GroupsPanel
                className="TestDirectory__GroupsPanel--modal"
                sampleCategory={sampleCategory}
                groups={groups}
                canModifyGroups
                existingGroupsReadonly={!canEditExistingGroups}
                activeKey={activeKey}
                onActiveKeyChange={setActiveKey}
                createDraft={{
                  name: groupName,
                  tests: groupTests,
                  onNameChange: (value) => {
                    setGroupName(value);
                    setError("");
                  },
                  onAddTests: addTests,
                  onRemoveTest: removeGroupTest,
                  onSave: handleSave,
                  onCancel: saving ? undefined : onClose,
                  saving,
                  error,
                }}
                onAddTestsToGroup={canEditExistingGroups ? addTestsToGroup : undefined}
                onRemoveTestFromGroup={canEditExistingGroups ? ({
                  group, testName, groupName: targetGroupName, key,
                }) => {
                  setActiveKey(key);
                  setRemoveTestTarget({ group, testName, groupName: targetGroupName });
                } : undefined}
                onRenameRequest={typeof onRename === "function" ? ({ group, name }) => {
                  setRenameTarget({ group, name });
                  setRenameValue(name);
                } : undefined}
                onDeleteRequest={typeof onDelete === "function" ? ({ group, name }) => {
                  setDeleteTarget({ group, name });
                } : undefined}
              />
            ) : null}
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(renameTarget)}
        closable
        centered
        footer={null}
        width="452px"
        wrapClassName="TestDirectoryModal TestDirectoryModal--renameGroup"
        className="TestDirectoryModal TestDirectoryModal--renameGroup"
        destroyOnHidden
        closeIcon={<CloseOutlined className="SampleSubmissionModal__closeIcon" />}
        onCancel={() => setRenameTarget(null)}
      >
        <div className="AddTestTitle">Rename group</div>
        <div className="AddTestInput">
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Enter group name"
            onPressEnter={submitRename}
          />
        </div>
        <div className="AddTestBtn">
          <Button
            type="default"
            htmlType="button"
            onClick={() => setRenameTarget(null)}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="button"
            onClick={submitRename}
          >
            Save
          </Button>
        </div>
      </Modal>

      {deleteTarget && (
        <SampleSubmissionConfirmModal
          headerText="Delete group"
          bodyText={(
            <>
              Are you sure you want to delete
              {" "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{deleteTarget.name}</span>
              ?
            </>
          )}
          buttonText={["Cancel", "Delete", "Deleting"]}
          buttonFunctions={[
            () => setDeleteTarget(null),
            async () => {
              if (typeof onDelete === "function") await onDelete(deleteTarget.group);
              setDeleteTarget(null);
            },
          ]}
        />
      )}

      {removeTestTarget && (
        <SampleSubmissionConfirmModal
          headerText="Remove test"
          bodyText={(
            <>
              Are you sure you want to remove
              {" "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{removeTestTarget.testName}</span>
              {" from "}
              <span className="sampleSubmissionConfirmDelTitleMessageHighlight">{removeTestTarget.groupName}</span>
              ?
            </>
          )}
          buttonText={["Cancel", "Remove", "Removing"]}
          buttonFunctions={[
            () => setRemoveTestTarget(null),
            () => {
              removeTestFromGroup(removeTestTarget.group, removeTestTarget.testName);
              setRemoveTestTarget(null);
            },
          ]}
        />
      )}
    </>
  );
}
